/**
 * SONE core — a canvas and the things on it (ADR-0043).
 *
 * The document's other shapes are sequences: a body is a list of blocks, a
 * collection is a list of rows. A canvas is not. Two items on it have no order
 * at all — they have positions — and everything here follows from that.
 *
 * So the items live in a `Y.Map` keyed by id, and each item is itself a `Y.Map`.
 * Moving something sets `x` and `y` on one key; two people moving two different
 * things write two keys and never meet. Two people moving the *same* thing is
 * last-writer-wins on that key, which is the honest outcome — there is no merge
 * of "here" and "there" that is not simply one of them.
 */
import * as Y from 'yjs';
import { DOC_KEYS } from './docSchema.js';
import { generateKeyBetween } from '../order/fractionalIndex.js';
/** What an item is. Its other keys depend on which. */
export const CANVAS_ITEM_KINDS = [
    'text',
    'image',
    'path',
    // Shapes. Three rather than a library of them: a rectangle, an ellipse and a
    // line are what people actually draw on a board, and every further shape is a
    // picture somebody could have drawn with the pen in less time than finding it
    // in a menu.
    'rect',
    'ellipse',
    'line',
];
export const CANVAS_KEYS = {
    kind: 'kind',
    x: 'x',
    y: 'y',
    /** Width and height, in canvas units. A path measures its own. */
    w: 'w',
    h: 'h',
    /**
     * Stacking, as a fractional index rather than a number or an array position.
     *
     * "Bring to front" is then one key written on one item — no renumbering, and
     * no argument with somebody reordering something else at the same time. The
     * same instrument the tree uses for siblings (ADR-0006).
     */
    z: 'z',
    /** A text item's words, as Y.Text so two people typing in one box merge. */
    text: 'text',
    /** An image item's file (ADR-0029). The workspace's file, not a copy. */
    fileId: 'fileId',
    /**
     * What the file was called and how large it is.
     *
     * Carried on the item rather than looked up, the same way a file block carries
     * them: the files panel names and measures what a page holds without a request
     * per file, and a board's pictures are things the page holds.
     */
    filename: 'filename',
    sizeBytes: 'sizeBytes',
    /** A path item's points, as a flat [x, y, x, y, …] array. */
    points: 'points',
    /** A path's or a shape's colour and thickness. */
    colour: 'colour',
    width: 'width',
    /** A shape's fill, or absent for an outline. */
    fill: 'fill',
    /** A text item's own size, in canvas units. Absent means the default. */
    size: 'size',
    /**
     * Whether this item can be moved, resized or rubbed out.
     *
     * A property of the item rather than of the person looking at it: a locked
     * thing is locked for everybody, because "locked for me only" is a note to
     * self that the next person cannot see and will move anyway.
     */
    locked: 'locked',
    /** How the board is ruled: 'dots', 'squares', 'lines' or 'plain'. */
    background: 'background',
};
/** What a board can be ruled with. */
export const CANVAS_BACKGROUNDS = ['dots', 'squares', 'lines', 'plain'];
/** How large a canvas may get, so one page cannot become the whole database. */
export const MAX_CANVAS_ITEMS = 2000;
/** Points in one stroke. A long scribble is fine; an infinite one is a bug. */
export const MAX_PATH_POINTS = 4000;
export function canvasMap(doc) {
    return doc.getMap(DOC_KEYS.canvas);
}
function asNumber(value, fallback = 0) {
    return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}
/** Read one item out of the document, or null if it is not one. */
export function readItem(id, map) {
    const kind = map.get(CANVAS_KEYS.kind);
    if (typeof kind !== 'string' || !CANVAS_ITEM_KINDS.includes(kind)) {
        // An item of a kind this version does not know is skipped rather than
        // guessed at — and, unlike a block, skipping does not delete it: it stays in
        // the map for a client that does know (ADR-0043).
        return null;
    }
    const text = map.get(CANVAS_KEYS.text);
    const points = map.get(CANVAS_KEYS.points);
    return {
        id,
        kind: kind,
        x: asNumber(map.get(CANVAS_KEYS.x)),
        y: asNumber(map.get(CANVAS_KEYS.y)),
        w: asNumber(map.get(CANVAS_KEYS.w), 160),
        h: asNumber(map.get(CANVAS_KEYS.h), 80),
        z: typeof map.get(CANVAS_KEYS.z) === 'string' ? map.get(CANVAS_KEYS.z) : 'a0',
        ...(text instanceof Y.Text ? { text: text.toString() } : {}),
        ...(typeof map.get(CANVAS_KEYS.fileId) === 'string'
            ? { fileId: map.get(CANVAS_KEYS.fileId) }
            : {}),
        ...(Array.isArray(points) ? { points: points.filter((n) => typeof n === 'number') } : {}),
        ...(typeof map.get(CANVAS_KEYS.colour) === 'string'
            ? { colour: map.get(CANVAS_KEYS.colour) }
            : {}),
        ...(typeof map.get(CANVAS_KEYS.width) === 'number'
            ? { width: map.get(CANVAS_KEYS.width) }
            : {}),
        ...(typeof map.get(CANVAS_KEYS.fill) === 'string'
            ? { fill: map.get(CANVAS_KEYS.fill) }
            : {}),
        ...(typeof map.get(CANVAS_KEYS.size) === 'number'
            ? { size: map.get(CANVAS_KEYS.size) }
            : {}),
        ...(map.get(CANVAS_KEYS.locked) === true ? { locked: true } : {}),
        ...(typeof map.get(CANVAS_KEYS.filename) === 'string'
            ? { filename: map.get(CANVAS_KEYS.filename) }
            : {}),
        ...(typeof map.get(CANVAS_KEYS.sizeBytes) === 'number'
            ? { sizeBytes: map.get(CANVAS_KEYS.sizeBytes) }
            : {}),
    };
}
/** Everything on the canvas, back to front. */
export function readCanvas(doc) {
    const items = [];
    canvasMap(doc).forEach((value, id) => {
        if (!(value instanceof Y.Map))
            return;
        const item = readItem(id, value);
        if (item)
            items.push(item);
    });
    // By the fractional index, and by id where two items somehow share one — a
    // stable order matters more than which of the two is on top.
    return items.sort((a, b) => (a.z === b.z ? a.id.localeCompare(b.id) : a.z < b.z ? -1 : 1));
}
/** The index that puts something in front of everything now on the canvas. */
export function frontIndex(doc) {
    const items = readCanvas(doc);
    const last = items[items.length - 1];
    return generateKeyBetween(last ? last.z : null, null);
}
/**
 * Put something on the canvas.
 *
 * In one transaction, so it arrives on another screen as one item rather than
 * as a position that briefly has nothing in it.
 */
export function addItem(doc, item) {
    const map = canvasMap(doc);
    if (map.size >= MAX_CANVAS_ITEMS)
        return;
    doc.transact(() => {
        const entry = new Y.Map();
        entry.set(CANVAS_KEYS.kind, item.kind);
        entry.set(CANVAS_KEYS.x, item.x);
        entry.set(CANVAS_KEYS.y, item.y);
        entry.set(CANVAS_KEYS.w, item.w ?? 200);
        entry.set(CANVAS_KEYS.h, item.h ?? 80);
        entry.set(CANVAS_KEYS.z, frontIndex(doc));
        if (item.kind === 'text') {
            const text = new Y.Text();
            if (item.text)
                text.insert(0, item.text);
            entry.set(CANVAS_KEYS.text, text);
        }
        if (item.fileId)
            entry.set(CANVAS_KEYS.fileId, item.fileId);
        if (item.points) {
            entry.set(CANVAS_KEYS.points, item.points.slice(0, MAX_PATH_POINTS));
        }
        if (item.colour)
            entry.set(CANVAS_KEYS.colour, item.colour);
        if (item.width !== undefined)
            entry.set(CANVAS_KEYS.width, item.width);
        if (item.fill)
            entry.set(CANVAS_KEYS.fill, item.fill);
        if (item.size !== undefined)
            entry.set(CANVAS_KEYS.size, item.size);
        if (item.filename)
            entry.set(CANVAS_KEYS.filename, item.filename);
        if (item.sizeBytes !== undefined)
            entry.set(CANVAS_KEYS.sizeBytes, item.sizeBytes);
        map.set(item.id, entry);
    });
}
/** Move something. Two numbers on one key, which is the whole point. */
export function moveItem(doc, id, x, y) {
    const entry = canvasMap(doc).get(id);
    if (!(entry instanceof Y.Map))
        return;
    doc.transact(() => {
        entry.set(CANVAS_KEYS.x, x);
        entry.set(CANVAS_KEYS.y, y);
    });
}
/**
 * Move several things by the same amount, in one transaction.
 *
 * One transaction rather than a loop of `moveItem`, and the difference is
 * visible on the other screen: five separate moves arrive as five updates and
 * are drawn one after another, so a group crawls across somebody else's board
 * instead of moving. It is also what makes undo treat the drag as one act.
 *
 * By a delta rather than by positions, because that is what a drag is — and
 * because it means two people dragging two overlapping groups still each move
 * their own by their own amount.
 */
export function moveItems(doc, ids, dx, dy) {
    const map = canvasMap(doc);
    doc.transact(() => {
        for (const id of ids) {
            const entry = map.get(id);
            if (!(entry instanceof Y.Map))
                continue;
            // A locked item is skipped rather than the whole move refused: dragging a
            // group that happens to contain one should move the rest.
            if (entry.get(CANVAS_KEYS.locked) === true)
                continue;
            entry.set(CANVAS_KEYS.x, asNumber(entry.get(CANVAS_KEYS.x)) + dx);
            entry.set(CANVAS_KEYS.y, asNumber(entry.get(CANVAS_KEYS.y)) + dy);
        }
    });
}
/** Resize something. */
export function resizeItem(doc, id, w, h) {
    const entry = canvasMap(doc).get(id);
    if (!(entry instanceof Y.Map))
        return;
    if (entry.get(CANVAS_KEYS.locked) === true)
        return;
    doc.transact(() => {
        entry.set(CANVAS_KEYS.w, Math.max(24, w));
        entry.set(CANVAS_KEYS.h, Math.max(24, h));
    });
}
/** Bring something to the front. One key, no renumbering. */
export function bringToFront(doc, id) {
    const entry = canvasMap(doc).get(id);
    if (!(entry instanceof Y.Map))
        return;
    const index = frontIndex(doc);
    doc.transact(() => entry.set(CANVAS_KEYS.z, index));
}
export function removeItem(doc, id) {
    doc.transact(() => canvasMap(doc).delete(id));
}
/**
 * Change one thing about one item.
 *
 * A single setter rather than one function per property: colour, thickness, fill
 * and text size are all "this item, this key, this value", and eight
 * near-identical functions would be eight places to forget a transaction.
 * Bounded to the keys that are safe to set this way — an item's kind and its
 * position have their own functions, and its id is not a property at all.
 */
export function styleItem(doc, id, changes) {
    const entry = canvasMap(doc).get(id);
    if (!(entry instanceof Y.Map))
        return;
    doc.transact(() => {
        if (changes.colour !== undefined)
            entry.set(CANVAS_KEYS.colour, changes.colour);
        if (changes.width !== undefined)
            entry.set(CANVAS_KEYS.width, changes.width);
        if (changes.size !== undefined)
            entry.set(CANVAS_KEYS.size, changes.size);
        // Null clears, which is how a filled shape becomes an outline again — the
        // same distinction a block's attributes make between "not set" and "set to
        // nothing".
        if (changes.fill === null)
            entry.delete(CANVAS_KEYS.fill);
        else if (changes.fill !== undefined)
            entry.set(CANVAS_KEYS.fill, changes.fill);
    });
}
/** Lock something, or let it go again. */
export function lockItem(doc, id, locked) {
    const entry = canvasMap(doc).get(id);
    if (!(entry instanceof Y.Map))
        return;
    doc.transact(() => {
        // Deleted rather than set to false: an item nobody has locked should carry
        // nothing, the same distinction every optional attribute here makes.
        if (locked)
            entry.set(CANVAS_KEYS.locked, true);
        else
            entry.delete(CANVAS_KEYS.locked);
    });
}
/**
 * Copy something, a little down and to the right.
 *
 * The offset is what makes a duplicate visible: a copy exactly on top of its
 * original looks like nothing happened, and the next drag moves whichever of
 * the two the hit test finds first.
 */
export function duplicateItem(doc, id, newId) {
    const source = canvasMap(doc).get(id);
    if (!(source instanceof Y.Map))
        return;
    const item = readItem(id, source);
    if (!item)
        return;
    addItem(doc, {
        id: newId,
        kind: item.kind,
        x: item.x + 16,
        y: item.y + 16,
        w: item.w,
        h: item.h,
        ...(item.text !== undefined ? { text: item.text } : {}),
        ...(item.fileId !== undefined ? { fileId: item.fileId } : {}),
        ...(item.points !== undefined ? { points: item.points } : {}),
        ...(item.colour !== undefined ? { colour: item.colour } : {}),
        ...(item.width !== undefined ? { width: item.width } : {}),
        ...(item.fill !== undefined ? { fill: item.fill } : {}),
        ...(item.size !== undefined ? { size: item.size } : {}),
        ...(item.filename !== undefined ? { filename: item.filename } : {}),
        ...(item.sizeBytes !== undefined ? { sizeBytes: item.sizeBytes } : {}),
    });
}
/**
 * How the board is ruled.
 *
 * On the canvas map rather than in each item, obviously — but as a key beside
 * the items rather than in a settings map of its own, because a map with one
 * entry is a map somebody has to remember exists.
 */
export function readBackground(doc) {
    // Typed as a map of items, and this one key is not one — read through
    // `unknown` rather than widening the map's type, which would make every other
    // reader check for a string.
    const value = canvasMap(doc).get(CANVAS_KEYS.background);
    return CANVAS_BACKGROUNDS.includes(value)
        ? value
        : 'dots';
}
export function setBackground(doc, background) {
    // Set as a plain string on the same map the items live in. `readCanvas` skips
    // it because it is not a Y.Map, which is the check that was already there for
    // anything unexpected.
    doc.transact(() => canvasMap(doc).set(CANVAS_KEYS.background, background));
}
/** The text on a canvas, for the search index — see ADR-0043 on the order. */
export function canvasText(doc) {
    return readCanvas(doc)
        .filter((item) => item.kind === 'text' && item.text)
        // Top to bottom, then left to right. A canvas has no reading order; this is
        // a fiction, and it is the fiction everybody already has.
        .sort((a, b) => (Math.abs(a.y - b.y) > 8 ? a.y - b.y : a.x - b.x))
        .map((item) => item.text)
        .join('\n');
}
//# sourceMappingURL=canvas.js.map